import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  kstToday,
  parsePrintSetupSnapshot,
  printSetupSnapshot,
  subscribePrintSetup,
  type PrintSetupState,
} from './printSetup';

export interface PrintSetupView extends PrintSetupState {
  done: boolean;
  hiddenToday: boolean;
}

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Milliseconds from `now` to the next KST midnight (Korea keeps no daylight saving), never under a second. */
export const msUntilNextKstDay = (now: Date): number => {
  const kst = now.getTime() + KST_OFFSET_MS;
  const nextMidnight = (Math.floor(kst / DAY_MS) + 1) * DAY_MS;
  return Math.max(1000, nextMidnight - kst);
};

/** This Chrome's label printer setup state, kept in step with every other reader of it (SSD-7389). */
export const usePrintSetup = (now: () => Date = () => new Date()): PrintSetupView => {
  const state = parsePrintSetupSnapshot(useSyncExternalStore(subscribePrintSetup, printSetupSnapshot));
  const nowRef = useRef(now);
  nowRef.current = now;
  const today = kstToday(now());
  const [, setWoke] = useState(0);
  // The store only emits on writes, so a page left open overnight would keep reading yesterday's date and the
  // reminder hidden for it; the hook wakes itself at the next KST midnight and reads the date again (SSD-7637).
  useEffect(() => {
    const timer = window.setTimeout(() => setWoke((n) => n + 1), msUntilNextKstDay(nowRef.current()));
    return () => window.clearTimeout(timer);
  }, [today]);
  return { ...state, done: state.doneOn !== null, hiddenToday: state.hiddenOn === today };
};
